import React, { useCallback, useEffect, useState } from 'react'
import { Box, Checkbox, FormControlLabel, Grid, Typography, Button, Collapse, TextField } from '@mui/material'
import { Link } from 'react-router-dom'
import { Controller } from 'react-hook-form'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import PasswordInput from './password-input'
import { ActionButton, PrivacyPolicyLink, TandCLink } from '../../../../components'
import { useAlert, useLocationScroll } from '../../../../hooks'
import { useDispatch, useSelector } from 'react-redux'
import { b2cSignupSlice, userSlice } from '../../../../reducers'
import CheckIcon from '@mui/icons-material/Check'
import { REVIEW_AND_PAYMENT_STEP } from '../progress-bar'
import useStyles from './styles'
import { v4 as uuidv4 } from 'uuid'
import { Auth } from '@aws-amplify/auth'
import { ErrorMultipleSubscriptions, ErrorSigningUp } from '../alerts'
import { useFormContext } from '../../context'
import clsx from 'clsx'

const AccountInfoForm = () => {
  const sx = useStyles()
  const [copyPasteError, setCopyPasteError] = React.useState(false)
  const currentStep = useSelector(b2cSignupSlice.selectors.selectCurrentStep)
  const [expandAccountDetails, setExpandAccountDetails] = useState(false)
  const profile = useSelector(userSlice.selectors.selectProfile)
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false)
  const account = useSelector(b2cSignupSlice.selectors.selectAccount)
  const disabled = useSelector(b2cSignupSlice.selectors.selectDisabled)
  const dispatch = useDispatch()
  const { enqueueMsg, enqueueAlert } = useAlert()
  const nameRef = useCallback((node) => {
    node?.focus()
  }, [])
  const [personalInfoSubmitted, setPersonalInfoSubmitted] = useState(false)
  const { personalInfoForm, paymentForm } = useFormContext()
  const { trigger, clearErrors } = personalInfoForm

  const toggleAccountDetails = () => {
    setExpandAccountDetails(!expandAccountDetails)
  }

  const handleFieldChange = ({ field, fieldState, onChange, onBlur }) => {
    return {
      onChange (e) {
        clearErrors(field.name)
        onChange && onChange(e)
        field.onChange(e)
      },
      onBlur (e) {
        field.onChange(e)
        onBlur && onBlur(e)
        if (e.target?.value?.length) {
          trigger(field.name)
        }
      }
    }
  }

  const isReviewAndPaymentStep = currentStep === REVIEW_AND_PAYMENT_STEP

  useEffect(() => {
    if (profile) {
      const { givenName, familyName, email, subscription, username } = profile

      const activeSubscription = subscription?.currentPeriodEndsAt && new Date(subscription.currentPeriodEndsAt) > new Date()
      setHasActiveSubscription(activeSubscription)

      personalInfoForm.setValue('firstName', givenName)
      personalInfoForm.setValue('lastName', familyName)

      personalInfoForm.setValue('email', email)
      personalInfoForm.setValue('confirmEmail', email)

      dispatch(b2cSignupSlice.actions.setAccount({
        firstName: givenName,
        lastName: familyName,
        email,
        username
      }))

      if (activeSubscription) {
        enqueueAlert(<ErrorMultipleSubscriptions />, { persist: false, autoHideDuration: 15000 })
        dispatch(b2cSignupSlice.actions.setDisabled(true))
        return
      }

      personalInfoForm.setValue('password', 'Redact@123')
      dispatch(b2cSignupSlice.actions.setCurrentStep(REVIEW_AND_PAYMENT_STEP))
    }
  }, [profile, personalInfoForm, paymentForm, dispatch, enqueueAlert])

  const submitPersonalInfo = async (event) => {
    event.preventDefault()
    setPersonalInfoSubmitted(true)

    try {
      const { firstName, lastName, email, password } = personalInfoForm.getValues()
      const randomUsername = uuidv4()
      const verifyUrl = window.location.host

      if (!profile) {
        await Auth.signUp({
          username: randomUsername,
          password,
          attributes: {
            email,
            given_name: firstName,
            family_name: lastName
          },
          clientMetadata: {
            verifyUrl
          }
        })

        dispatch(b2cSignupSlice.actions.setAccount({
          firstName,
          lastName,
          email,
          username: randomUsername
        }))

        dispatch(b2cSignupSlice.actions.setCurrentStep(REVIEW_AND_PAYMENT_STEP))
        return
      }

      if (hasActiveSubscription) {
        enqueueAlert(<ErrorMultipleSubscriptions />, { persist: false, autoHideDuration: 15000 })
        setPersonalInfoSubmitted(false)
      }
    } catch (err) {
      console.error(err)
      if (err?.message?.includes('User already exists')) {
        enqueueAlert(<ErrorSigningUp />, { persist: false, autoHideDuration: 15000 })
      } else {
        enqueueMsg('An unexpected error happened while creating your account', 'error')
      }
    } finally {
      setPersonalInfoSubmitted(false)
    }
  }

  return (
    <Box className={sx.root}>
      <form onSubmit={submitPersonalInfo}>
        <Grid spacing={1} sx={{ mt: 2 }}>
          <>
            {isReviewAndPaymentStep
              ? (
                <Box display='flex' alignItems='center' justifyContent='space-between' gap={2} sx={{ mb: 2 }}>
                  <Box display='flex' alignItems='center'>
                    <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px', marginRight: '16px', width: '58px', height: '58px', justifyContent: 'center', bgcolor: '#DEDEF1', color: '#fff', borderRadius: '12px', fontWeight: 'bold' }}><CheckIcon sx={{ color: '#ABABE2' }} /></Box>
                    <Typography variant='h6' fontWeight='bold'>Create an account</Typography>
                  </Box>
                  {(account?.firstName && account?.lastName && account?.email && !disabled) && (
                    <Typography variant='caption'><Button onClick={toggleAccountDetails}>See details</Button></Typography>
                  )}
                </Box>
                )
              : (
                <Box display='flex' alignItems='center' gap={2} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px', width: '58px', height: '58px', justifyContent: 'center', bgcolor: '#5D5BED', color: '#fff', borderRadius: '12px', fontWeight: 'bold' }}>STEP 1</Box>
                  <Box>
                    <Typography variant='h6' className={clsx(sx.title, currentStep === REVIEW_AND_PAYMENT_STEP && sx.titleDisabled)} fontWeight='bold'>Create an account</Typography>
                    <Typography variant='caption'>
                      Already have an account? <Link to='/login'>Sign in</Link>
                    </Typography>
                  </Box>
                </Box>
                )}
          </>

          <Collapse in={(!isReviewAndPaymentStep || expandAccountDetails)} timeout={500}>
            <Grid container direction='column' spacing={1}>
              <Grid item xs={12}>
                <Grid container direction='row' spacing={1}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name='firstName'
                      control={personalInfoForm.control}
                      render={({ field, fieldState }) => (
                        <TextField
                          id='FirstName'
                          label='First name'
                          inputRef={nameRef}
                          variant='outlined'
                          InputLabelProps={{ required: false }}
                          disabled={isReviewAndPaymentStep || personalInfoSubmitted || disabled}
                          fullWidth
                          required
                          className={sx.textfield}
                          error={!!fieldState.error}
                          helperText={fieldState?.error?.message}
                          {...handleFieldChange({ field, fieldState })}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name='lastName'
                      control={personalInfoForm.control}
                      render={({ field, fieldState }) => (
                        <TextField
                          id='LastName'
                          label='Last name'
                          variant='outlined'
                          InputLabelProps={{ required: false }}
                          disabled={isReviewAndPaymentStep || personalInfoSubmitted || disabled}
                          fullWidth
                          required
                          className={sx.textfield}
                          error={!!fieldState.error}
                          helperText={fieldState?.error?.message}
                          {...handleFieldChange({ field, fieldState })}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container direction='row' spacing={1}>
                  <Grid item xs={12} mb={1} md={6}>
                    <Controller
                      name='email'
                      control={personalInfoForm.control}
                      render={({ field, fieldState }) => (
                        <TextField
                          label='Email'
                          variant='outlined'
                          fullWidth
                          required
                          InputLabelProps={{ required: false }}
                          className={sx.textfield}
                          error={!!fieldState.error}
                          {...handleFieldChange({ field, fieldState })}
                          helperText={fieldState?.error?.message}
                          disabled={isReviewAndPaymentStep || personalInfoSubmitted || disabled}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} mb={1} md={6}>
                    <Controller
                      name='confirmEmail'
                      control={personalInfoForm.control}
                      render={({ field, fieldState }) => (
                        <TextField
                          label='Confirm Email'
                          variant='outlined'
                          fullWidth
                          required
                          InputLabelProps={{ required: false }}
                          className={sx.textfield}
                          error={!!fieldState.error || !!copyPasteError}
                          name={field.name}
                          ref={field.ref}
                          {...handleFieldChange({ field, fieldState, onChange: (e) => (setCopyPasteError(false)) })}
                          onPaste={(e) => {
                            setCopyPasteError(true)
                            e.preventDefault()
                          }}
                          disabled={isReviewAndPaymentStep || personalInfoSubmitted || disabled}
                          helperText={
                  copyPasteError
                    ? 'Typing your email again helps us verify accuracy. Please enter it manually.'
                    : fieldState?.error?.message ?? ''
                }
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
              {!isReviewAndPaymentStep && (
                <>
                  <Grid item xs={12}>
                    <Box className={sx.confirmationText}>
                      <ErrorOutlineIcon className={sx.confirmationTextIcon} />
                      <Typography>
                        An email with a confirmation link will be sent to your inbox. Once received, please follow the
                        instructions in the email to activate your account.
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item mt={2} xs={12}>
                    <Controller
                      name='password'
                      control={personalInfoForm.control}
                      render={({ field, fieldState }) => <PasswordInput field={field} fieldState={fieldState} disabled={personalInfoSubmitted || disabled} styleClasses={sx.textfield} required={false} />}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant='caption'>
                      * All fields are required
                    </Typography>
                  </Grid>

                  <Grid item mt={1} xs={12}>
                    <FormControlLabel
                      control={
                        <Controller
                          name='acceptTerms'
                          control={personalInfoForm.control}
                          render={({ field }) => <Checkbox color='primary' {...field} checked={field.value} disabled={personalInfoSubmitted || disabled} />}
                        />
                  }
                      label={
                        <Typography variant='caption'>
                          By creating an account, you agree to our{' '}
                          <PrivacyPolicyLink state={{ [useLocationScroll.SCROLLTOP]: true }} data-test='privacy-policy-link'>
                            Privacy Policy
                          </PrivacyPolicyLink>{' '}
                          and{' '}
                          <TandCLink state={{ [useLocationScroll.SCROLLTOP]: true }} data-test='tandc-link'>
                            Terms of Use
                          </TandCLink>
                          .<br />
                          <Typography variant='caption'>
                            This includes receiving marketing emails, you can unsubscribe at any time.
                          </Typography>
                        </Typography>
                    }
                    />
                  </Grid>

                  <Grid item>
                    <ActionButton
                      label='Continue to payment'
                      variant='primary'
                      type='submit'
                      loading={personalInfoSubmitted}
                      disabled={personalInfoSubmitted || !personalInfoForm.formState.isValid || disabled}
                      className={sx.submitButton}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Collapse>

          {/* )} */}
        </Grid>
      </form>
    </Box>
  )
}

export default AccountInfoForm
